import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  Phone, Clock, CheckCircle, Shield, Headphones, Globe, Plane,
  Users, Star, ArrowRight, MapPin, CreditCard, Luggage, Calendar,
  AlertTriangle
} from "lucide-react";
import { useContact } from "../context/ContactContext";
import "./AirlineReservation.css";

const AirlineReservation = () => {
  const { contactSettings } = useContact();
  const tfn = contactSettings.tfn || "+1-888-859-0441";
  const tfnClean = tfn.replace(/[^0-9+]/g, "");
  const [openFaq, setOpenFaq] = useState(null);

  const airlines = [
    { name: "American Airlines", code: "AA", color: "#0078D2", founded: "1930", hub: "Dallas/Fort Worth" },
    { name: "Delta Air Lines",   code: "DL", color: "#003A70", founded: "1924", hub: "Atlanta" },
    { name: "United Airlines",   code: "UA", color: "#002244", founded: "1926", hub: "Chicago O'Hare" },
    { name: "Southwest Airlines",code: "WN", color: "#304CB2", founded: "1967", hub: "Dallas Love Field" },
    { name: "JetBlue Airways",   code: "B6", color: "#003876", founded: "1998", hub: "New York JFK" },
    { name: "Alaska Airlines",   code: "AS", color: "#01426A", founded: "1932", hub: "Seattle-Tacoma" },
    { name: "Air Canada",        code: "AC", color: "#D01523", founded: "1937", hub: "Toronto Pearson" },
    { name: "WestJet",           code: "WS", color: "#00A1DE", founded: "1996", hub: "Calgary" },
    { name: "Spirit Airlines",   code: "NK", color: "#FFC510", founded: "1983", hub: "Fort Lauderdale" },
    { name: "Frontier Airlines", code: "F9", color: "#004225", founded: "1994", hub: "Denver" },
    { name: "British Airways",   code: "BA", color: "#075AAA", founded: "1974", hub: "London Heathrow" },
    { name: "Emirates",          code: "EK", color: "#C60C30", founded: "1985", hub: "Dubai" },
  ];

  const services = [
    {
      icon: <Plane size={36} />,
      title: "New Flight Reservations",
      description: "Book one-way, round-trip, or multi-city flights on all major airlines. Get the best available fares for any destination worldwide.",
      color: "#0057b8",
    },
    {
      icon: <Calendar size={36} />,
      title: "Change & Modify Bookings",
      description: "Need to change your travel dates, departure city, or destination? Our agents can modify your existing reservation quickly and efficiently.",
      color: "#00a86b",
    },
    {
      icon: <Shield size={36} />,
      title: "Cancellation & Refunds",
      description: "Understanding airline cancellation policies can be complex. We help you cancel your booking and navigate the refund or travel credit process.",
      color: "#e85d04",
    },
    {
      icon: <Luggage size={36} />,
      title: "Baggage & Seat Upgrades",
      description: "Add checked bags, upgrade to premium seats, or request special assistance including wheelchair service and unaccompanied minor travel.",
      color: "#7209b7",
    },
    {
      icon: <Users size={36} />,
      title: "Group Bookings",
      description: "Traveling with a group of 10 or more? Get special group rates and dedicated support for corporate travel, family reunions, and events.",
      color: "#d62839",
    },
    {
      icon: <CreditCard size={36} />,
      title: "Miles & Loyalty Programs",
      description: "Earn and redeem frequent flyer miles, upgrade using points, and manage your loyalty program accounts across all major airline programs.",
      color: "#0077b6",
    },
    {
      icon: <MapPin size={36} />,
      title: "Last-Minute Flights",
      description: "Need to travel urgently? We specialize in finding available seats on last-minute flights and can handle emergency travel bookings 24/7.",
      color: "#fb8500",
    },
    {
      icon: <Globe size={36} />,
      title: "International Travel",
      description: "Book international flights and get guidance on visa requirements, travel documentation, transit stops, and destination entry policies.",
      color: "#06d6a0",
    },
  ];

  const steps = [
    {
      number: "01",
      icon: <Phone size={44} />,
      title: "Call Our Reservation Line",
      desc: `Dial ${tfn} to reach a live airline reservation specialist instantly. No robots, no waiting queues — just real help.`,
    },
    {
      number: "02",
      icon: <Headphones size={44} />,
      title: "Share Your Travel Details",
      desc: "Tell us your travel dates, origin, destination, number of passengers, and any preferences like seat class or airline loyalty program.",
    },
    {
      number: "03",
      icon: <CheckCircle size={44} />,
      title: "Get Your Booking Confirmed",
      desc: "We search for the best available options, confirm your reservation, and send you a booking confirmation — all in minutes.",
    },
  ];

  const faqs = [
    {
      q: "How do I make an airline reservation by phone?",
      a: `Simply call ${tfn} to speak with a live reservation specialist. Have your travel dates, preferred airline, and passenger details ready. Our agent will find available flights and complete your booking quickly.`,
    },
    {
      q: "Can I book flights for multiple passengers over the phone?",
      a: `Yes! Call ${tfn} and our agents can book flights for families, couples, or large groups. We handle all passenger details, seat selection, and special requirements in one call.`,
    },
    {
      q: "Is it cheaper to book a flight over the phone?",
      a: `Our agents have access to a wide range of fare classes and can help identify the best available pricing. In some cases, especially for complex itineraries or last-minute travel, phone booking can result in better options. Call ${tfn} to compare.`,
    },
    {
      q: "How far in advance should I book a flight?",
      a: "For domestic flights, booking 3–6 weeks in advance is ideal. For international flights, 2–6 months ahead typically yields the best fares. That said, last-minute deals do exist — call us anytime and we'll find what's available.",
    },
    {
      q: "Can I change or cancel a flight I booked over the phone?",
      a: `Absolutely. Call ${tfn} and our agents will look up your reservation and assist with changes, date modifications, or cancellations based on the airline's current policies and your ticket type.`,
    },
    {
      q: "What information do I need to make a flight reservation?",
      a: `You'll need: full passenger names (as on government ID), travel dates, departure and arrival cities, preferred class of travel, and a valid payment method. Having your frequent flyer number handy is a plus. Call ${tfn} and we'll guide you through the rest.`,
    },
    {
      q: "Do you book international flights?",
      a: `Yes! We handle both domestic and international flight reservations. Our agents can help with flights to destinations worldwide and advise on connecting flights, layovers, and required travel documents. Call ${tfn} for international booking assistance.`,
    },
    {
      q: "What are your reservation hours?",
      a: `Our airline reservation helpline at ${tfn} is available 24 hours a day, 7 days a week — including holidays. Travel doesn't stop, and neither do we.`,
    },
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      location: "Toronto, ON",
      rating: 5,
      text: "I called to book a last-minute flight to Miami and was connected to an agent within seconds. Had my tickets confirmed in under 10 minutes. Absolutely seamless!",
    },
    {
      name: "James R.",
      location: "Calgary, AB",
      rating: 5,
      text: "Needed to change my return date after my meeting got extended. The agent handled everything quickly and even found me a better connection. Will use again.",
    },
    {
      name: "Priya K.",
      location: "Vancouver, BC",
      rating: 5,
      text: "Booked flights for a family of 6 which always feels overwhelming. The agent was patient, professional, and got us great seats together. Highly recommend.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Airline Reservation | Book Flights by Phone – Call {tfn}</title>
        <meta
          name="description"
          content={`Book airline reservations by phone with ease. Call ${tfn} for 24/7 flight booking assistance on all major airlines. New reservations, changes, cancellations & group bookings.`}
        />
        <meta
          name="keywords"
          content="airline reservation, book flight by phone, airline booking number, flight reservation phone number, book airline ticket, airline booking assistance, flight booking helpline, 24/7 airline reservation"
        />
        <link rel="canonical" href="https://mfdholidays.ca/airline-reservation" />
        <meta property="og:title" content={`Airline Reservation – Call ${tfn} | MFD Holidays`} />
        <meta property="og:description" content="Book flights, change reservations, and get 24/7 airline booking support. Speak to a live travel specialist now." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mfdholidays.ca/airline-reservation" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            "name": "MFD Holidays – Airline Reservations",
            "url": "https://mfdholidays.ca/airline-reservation",
            "telephone": tfn,
            "openingHours": "Mo-Su 00:00-24:00",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": tfn,
              "contactType": "reservations",
              "areaServed": ["CA", "US"],
              "availableLanguage": ["English"]
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a },
            })),
          })}
        </script>
      </Helmet>

      <div className="ar-page">

        {/* ── HERO ── */}
        <section className="ar-hero">
          <div className="ar-hero__bg">
            <img
              src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80"
              alt="Commercial airplane flying above clouds"
              className="ar-hero__img"
            />
            <div className="ar-hero__overlay" />
          </div>
          <div className="ar-hero__content">
            <div className="ar-hero__badge">
              <Headphones size={18} />
              <span>24/7 Live Reservation Agents</span>
            </div>
            <h1 className="ar-hero__title">
              Airline Reservation <span>Made Simple</span>
            </h1>
            <p className="ar-hero__sub">
              Book flights, change your itinerary, or cancel your ticket — all with one call.
              Real agents, real results, available 24 hours a day.
            </p>
            <a href={`tel:${tfnClean}`} className="ar-hero__cta">
              <Phone size={22} />
              <span>Call Now: {tfn}</span>
            </a>
            <div className="ar-hero__pills">
              <span><CheckCircle size={15} /> No Hold Music</span>
              <span><CheckCircle size={15} /> Live Agents Only</span>
              <span><CheckCircle size={15} /> All Major Airlines</span>
              <span><CheckCircle size={15} /> 24/7 Available</span>
            </div>
          </div>
        </section>

        {/* ── STATS BAR ── */}
        <div className="ar-stats">
          <div className="ar-stats__inner">
            {[
              { n: "200+", l: "Airlines Supported" },
              { n: "60K+", l: "Bookings Completed" },
              { n: "4.9★", l: "Average Rating" },
              { n: "< 30s", l: "Avg. Connect Time" },
              { n: "24/7",  l: "Always Available" },
            ].map((s, i) => (
              <div className="ar-stats__item" key={i}>
                <span className="ar-stats__num">{s.n}</span>
                <span className="ar-stats__lbl">{s.l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── INTRO SPLIT ── */}
        <section className="ar-section ar-intro">
          <div className="ar-container ar-intro__grid">
            <div className="ar-intro__text">
              <h2>Your Trusted Airline Reservation Service</h2>
              <p>
                Making an <strong>airline reservation</strong> should be fast and stress-free. Yet navigating airline websites, comparing dozens of fares, and deciphering baggage fees often turns a simple booking into an hours-long ordeal.
              </p>
              <p>
                That's where we come in. At MFD Holidays, our dedicated team of travel specialists handles airline reservations for thousands of customers every month. Whether you're booking a quick weekend getaway, a cross-country business trip, or a once-in-a-lifetime international adventure, one call to <strong>{tfn}</strong> puts a knowledgeable agent on your side instantly.
              </p>
              <p>
                We work with all major North American and international carriers, giving you access to a broad inventory of flights, fare classes, and seat options — without the frustration of clicking through endless booking screens.
              </p>
              <a href={`tel:${tfnClean}`} className="ar-btn ar-btn--primary">
                <Phone size={18} /> Call {tfn}
              </a>
            </div>
            <div className="ar-intro__img">
              <img
                src="https://images.unsplash.com/photo-1521727284390-39ca72a09ac2?w=700&q=80"
                alt="Travel specialist assisting customer with airline reservation"
              />
              <div className="ar-intro__img-badge">
                <Clock size={18} />
                <span>Available 24/7 — Even Holidays</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── SERVICES ── */}
        <section className="ar-section ar-services">
          <div className="ar-container">
            <div className="ar-section__head">
              <h2>What Our Reservation Specialists Can Do For You</h2>
              <p>
                From simple one-way bookings to complex multi-leg itineraries, call <strong>{tfn}</strong> for expert help with:
              </p>
            </div>
            <div className="ar-services__grid">
              {services.map((s, i) => (
                <div className="ar-service-card" key={i} style={{ "--accent": s.color }}>
                  <div className="ar-service-card__icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.description}</p>
                  <a href={`tel:${tfnClean}`} className="ar-service-card__link">
                    Call Now <ArrowRight size={14} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="ar-section ar-steps">
          <div className="ar-container">
            <div className="ar-section__head ar-section__head--light">
              <h2>How to Book Your Flight in 3 Easy Steps</h2>
              <p>Getting your airline reservation has never been simpler.</p>
            </div>
            <div className="ar-steps__grid">
              {steps.map((s, i) => (
                <div className="ar-step" key={i}>
                  <div className="ar-step__num">{s.number}</div>
                  <div className="ar-step__icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
            <div className="ar-steps__cta">
              <a href={`tel:${tfnClean}`} className="ar-btn ar-btn--white">
                <Phone size={20} /> Start Your Reservation — {tfn}
              </a>
            </div>
          </div>
        </section>

        {/* ── AIRLINES ── */}
        <section className="ar-section ar-airlines">
          <div className="ar-container">
            <div className="ar-section__head">
              <h2>We Book Flights on All Major Airlines</h2>
              <p>
                Our agents are experienced with every major carrier. No matter which airline you prefer, call <strong>{tfn}</strong> and we'll take care of your reservation.
              </p>
            </div>
            <div className="ar-airlines__grid">
              {airlines.map((a, i) => (
                <div className="ar-airline-card" key={i} style={{ "--border": a.color }}>
                  <div className="ar-airline-card__logo">
                    <img
                      src={`https://images.kiwi.com/airlines/64/${a.code}.png`}
                      alt={`${a.name} logo`}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </div>
                  <div className="ar-airline-card__info">
                    <strong>{a.name}</strong>
                    <span>Hub: {a.hub}</span>
                  </div>
                  <a href={`tel:${tfnClean}`} className="ar-airline-card__btn">
                    <Phone size={13} /> Book Now
                  </a>
                </div>
              ))}
            </div>
            <p className="ar-airlines__note">
              Don't see your airline? We work with 200+ carriers worldwide.{" "}
              <a href={`tel:${tfnClean}`}>Call {tfn}</a> for any airline.
            </p>
          </div>
        </section>

        {/* ── CTA BANNER ── */}
        <section className="ar-cta-banner">
          <div className="ar-container">
            <div className="ar-cta-banner__inner">
              <div className="ar-cta-banner__img">
                <img
                  src="https://images.unsplash.com/photo-1529074963764-98f45c47344b?w=700&q=80"
                  alt="Multiple airplanes at airport terminal ready for departure"
                />
              </div>
              <div className="ar-cta-banner__text">
                <h2>Ready to Book Your Next Flight?</h2>
                <p>
                  Skip the lengthy online booking process. One call to our reservation line gets you a confirmed ticket in minutes — with a live agent guiding you every step of the way.
                </p>
                <ul className="ar-cta-banner__list">
                  <li><CheckCircle size={18} /> Access to 200+ airlines worldwide</li>
                  <li><CheckCircle size={18} /> Best available fares searched for you</li>
                  <li><CheckCircle size={18} /> Seat selection & special requests handled</li>
                  <li><CheckCircle size={18} /> Instant confirmation via email</li>
                  <li><CheckCircle size={18} /> Support even after booking</li>
                </ul>
                <a href={`tel:${tfnClean}`} className="ar-btn ar-btn--primary ar-btn--lg">
                  <Phone size={22} /> {tfn}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="ar-section ar-testimonials">
          <div className="ar-container">
            <div className="ar-section__head">
              <h2>What Travelers Are Saying</h2>
              <p>Thousands of satisfied customers trust us with their airline reservations.</p>
            </div>
            <div className="ar-testimonials__grid">
              {testimonials.map((t, i) => (
                <div className="ar-testimonial" key={i}>
                  <div className="ar-testimonial__stars">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} size={16} fill="#f59e0b" color="#f59e0b" />
                    ))}
                  </div>
                  <p className="ar-testimonial__text">"{t.text}"</p>
                  <div className="ar-testimonial__author">
                    <strong>{t.name}</strong>
                    <span><MapPin size={12} /> {t.location}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="ar-section ar-faq">
          <div className="ar-container">
            <div className="ar-section__head">
              <h2>Frequently Asked Questions</h2>
              <p>Everything you need to know about making an airline reservation with us.</p>
            </div>
            <div className="ar-faq__list">
              {faqs.map((f, i) => (
                <div
                  className={`ar-faq__item${openFaq === i ? " ar-faq__item--open" : ""}`}
                  key={i}
                >
                  <button
                    className="ar-faq__q"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    {f.q}
                    <span className="ar-faq__arrow">{openFaq === i ? "▲" : "▼"}</span>
                  </button>
                  {openFaq === i && <p className="ar-faq__a">{f.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="ar-final-cta">
          <div className="ar-container">
            <Plane size={52} className="ar-final-cta__icon" />
            <h2>Book Your Airline Reservation Right Now</h2>
            <p>
              Our travel specialists are standing by 24/7. One call and your flights are booked.
            </p>
            <a href={`tel:${tfnClean}`} className="ar-final-cta__btn">
              <Phone size={28} />
              <div>
                <span>Call Toll-Free 24/7</span>
                <strong>{tfn}</strong>
              </div>
            </a>
            <div className="ar-final-cta__pills">
              <span>✓ Live Agents</span>
              <span>✓ No Wait</span>
              <span>✓ All Airlines</span>
              <span>✓ Instant Confirmation</span>
            </div>
          </div>
        </section>

        {/* ── DISCLAIMER ── */}
        <section className="ar-disclaimer">
          <div className="ar-container">
            <div className="ar-disclaimer__box">
              <div className="ar-disclaimer__head">
                <AlertTriangle size={20} />
                <h3>Important Disclaimer</h3>
              </div>
              <p>
                <strong>MFD Holidays is an independent travel assistance and booking service.</strong> We are not the official reservations desk, customer service line, or website of any airline. All airline names, logos, and trademarks displayed on this page are the property of their respective owners and are used solely for identification purposes.
              </p>
              <p>
                Fares, availability, and policies are subject to change without notice and are governed entirely by the airline. By calling our helpline, you are contacting an independent travel agency that may charge service fees for booking assistance. Always verify fare details and terms directly with the airline before completing a purchase.
              </p>
              <p>
                Our agents provide general travel guidance and assistance. For official airline policies regarding cancellations, refunds, baggage, and frequent flyer programs, we recommend visiting the airline's official website. MFD Holidays does not guarantee specific fares or seat availability.
              </p>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default AirlineReservation;
